import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { applyTheme } from "@/contexts/TenantThemeContext";
import { FORM_TRANSLATIONS, LANGUAGES } from "@/lib/checkInTranslations";
import { CaretLeft, CircleNotch } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicPrivacyPage() {
  const { tenantSlug, propertyId } = useParams();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [lang, setLang] = useState(params.get("lang") || "hr");

  useEffect(() => {
    document.title = "Politika privatnosti";
    const url = propertyId
      ? `${API_BASE}/public/check-in/${tenantSlug}/p/${propertyId}/meta`
      : `${API_BASE}/public/check-in/${tenantSlug}/meta`;
    axios
      .get(url)
      .then((r) => {
        setMeta(r.data);
        if (r.data.tenant) applyTheme(r.data.tenant);
      });
  }, [tenantSlug, propertyId]);

  const t = useMemo(() => (FORM_TRANSLATIONS[lang] || FORM_TRANSLATIONS.hr).privacy, [lang]);

  if (!meta) {
    return <div className="flex min-h-screen items-center justify-center"><CircleNotch size={32} className="animate-spin text-primary" /></div>;
  }

  const tenant = meta.tenant;
  const brandFont = tenant?.heading_font ? `"${tenant.heading_font}", serif` : undefined;
  const entities = meta.legal_entities || [];
  const primary = entities[0] || {};

  return (
    <div className="min-h-screen bg-[var(--rw-bg-page,hsl(var(--background)))]" data-testid="public-privacy-page">
      <header className="border-b border-border bg-[var(--rw-bg-white,hsl(var(--card)))]">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.brand_name} className="h-10 w-auto" />
          ) : (
            <div className="text-xl font-bold text-foreground" style={{ fontFamily: brandFont }}>{tenant?.brand_name || tenant?.name}</div>
          )}
          <div className="flex gap-1.5">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${lang === l.code ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted"}`}
                data-testid={`pp-lang-${l.code}`}
              >
                <span>{l.flag}</span> {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8">
        <Link to={`/check-in/${tenantSlug}${propertyId ? `/p/${propertyId}` : ""}`} className="mb-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="privacy-back">
          <CaretLeft size={14} /> {t.back}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: brandFont }}>{t.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t.subtitle}</p>

        <section className="mt-6 space-y-2">
          <h2 className="text-lg font-bold text-foreground">{t.h1}</h2>
          {entities.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.controllers_empty}</p>
          ) : (
            <>
              <p className="text-sm text-foreground">{t.h1_body(primary)}</p>
              {entities.length > 1 && (
                <details className="mt-3 rounded-md border border-border bg-card p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-foreground">{t.controllers_title}</summary>
                  <div className="mt-3 space-y-3">
                    {entities.map((e, i) => (
                      <div key={i} className="rounded border border-border/60 p-3 text-xs text-foreground">
                        <div className="font-semibold">{e.company_name || e.responsible_person || "—"}</div>
                        {e.oib && <div>OIB: {e.oib}</div>}
                        {e.address && <div>{e.address}{e.city ? `, ${e.city}` : ""}{e.postal_code ? ` ${e.postal_code}` : ""}</div>}
                        {e.email && <div>Email: <a className="text-primary" href={`mailto:${e.email}`}>{e.email}</a></div>}
                        {e.phone && <div>Tel: {e.phone}</div>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </section>

        <Section title={t.h2}>
          <Bullets items={t.h2_body} />
        </Section>
        <Section title={t.h3}>
          <Bullets items={t.h3_body} />
        </Section>
        <Section title={t.h5}>
          <p className="text-sm text-foreground">{t.h5_body}</p>
        </Section>
        <Section title={t.h7}>
          <p className="text-sm text-foreground">{t.h7_body}</p>
        </Section>
        <Section title={t.h8}>
          <Bullets items={t.h8_body} />
        </Section>
        <Section title={t.h10}>
          <Bullets items={t.h10_body} />
        </Section>

        <Section title={t.h12}>
          {primary && (primary.email || primary.phone) ? (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              {primary.company_name && <div className="font-semibold text-foreground">{primary.company_name}</div>}
              {primary.responsible_person && <div className="text-foreground">{primary.responsible_person}</div>}
              {primary.address && <div className="text-muted-foreground">{primary.address}{primary.city ? `, ${primary.city}` : ""}</div>}
              {primary.oib && <div className="text-muted-foreground">OIB: {primary.oib}</div>}
              {primary.email && <div className="text-muted-foreground">Email: <a className="text-primary" href={`mailto:${primary.email}`}>{primary.email}</a></div>}
              {primary.phone && <div className="text-muted-foreground">Tel: {primary.phone}</div>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t.controllers_empty}</p>
          )}
        </Section>

        <p className="mt-8 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">{t.supervisory}</p>
      </main>

      <footer className="mt-12 border-t border-border py-6 text-center text-xs text-muted-foreground">
        Powered by <span className="font-semibold text-foreground">Lodgy</span>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-6 space-y-2">
      <h2 className="text-lg font-bold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
      {(items || []).map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}
